import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBuilding } from "../../context/BuildingContext";
import { journalApi, categoriesApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import PageHeader from "../../components/common/PageHeader";

interface JournalLineForm {
  id: string;
  nominalCode: string;
  categoryName: string;
  description: string;
  debit: number;
  credit: number;
}

interface JournalEntryForm {
  date: string;
  description: string;
  reference: string;
  lines: JournalLineForm[];
}

const today = new Date().toISOString().split("T")[0];

const emptyLine = (): JournalLineForm => ({
  id:
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2),
  nominalCode: "",
  categoryName: "",
  description: "",
  debit: 0,
  credit: 0,
});

const defaultForm: JournalEntryForm = {
  date: today,
  description: "",
  reference: "",
  lines: [emptyLine(), emptyLine()],
};

const JournalNewEntry: React.FC = () => {
  const navigate = useNavigate();
  const { currentBuilding } = useBuilding();

  const [form, setForm] = useState<JournalEntryForm>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allCategories, setAllCategories] = useState<
    { nominalCode: string; name: string; parentCode?: string }[]
  >([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const cats = await categoriesApi.getAll();
        setAllCategories(Array.isArray(cats) ? cats : []);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
    fetchCategories();
  }, []);

  const totalDebit = form.lines.reduce((s, l) => s + (l.debit || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + (l.credit || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const handleLineChange = (
    index: number,
    field: keyof JournalLineForm,
    value: string | number,
  ) => {
    setForm((prev) => {
      const lines = [...prev.lines];
      lines[index] = { ...lines[index], [field]: value };
      if (field === "nominalCode") {
        const cat = allCategories.find((c) => c.nominalCode === value);
        if (cat) lines[index].categoryName = cat.name;
      }
      return { ...prev, lines };
    });
  };

  const addLine = () => {
    setForm((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }));
  };

  const removeLine = (index: number) => {
    if (form.lines.length <= 2) return;
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== index),
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentBuilding?.id) {
      setError("No building selected. Please select a building first.");
      return;
    }
    if (!isBalanced) {
      setError(
        "Journal entry is unbalanced. Total debits must equal total credits.",
      );
      return;
    }
    if (form.lines.length < 2) {
      setError("Journal entries must have at least 2 lines.");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      // Convert line amounts from pounds to pence
      const linesInPence = form.lines.map((l) => ({
        ...l,
        debit: Math.round(l.debit * 100),
        credit: Math.round(l.credit * 100),
      }));
      await journalApi.createEntry({
        buildingId: currentBuilding.id,
        date: form.date,
        description: form.description,
        reference: form.reference,
        lines: linesInPence,
      });
      navigate("/dashboard/journal");
    } catch (err) {
      console.error("Error creating journal entry:", err);
      setError("Failed to create journal entry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitting) {
    return <Spinner message="Creating journal entry..." />;
  }

  return (
    <div>
      <PageHeader
        title="New Journal Entry"
        description={`Record a financial transaction for ${
          currentBuilding?.name || "your building"
        }`}
        actions={
          <button
            onClick={() => navigate("/dashboard/journal")}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 sm:py-2 border border-gray-300 rounded-md shadow-sm text-base sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none min-h-[44px] sm:min-h-0"
          >
            Cancel
          </button>
        }
      />

      {error && (
        <div className="mt-4">
          <ErrorAlert message={error} />
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="mt-8 bg-white shadow overflow-hidden rounded-lg"
      >
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Entry Details
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Fill in the details below to add a new journal entry.
          </p>
        </div>

        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            {/* Description */}
            <div className="sm:col-span-2">
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700"
              >
                Description <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="description"
                name="description"
                required
                value={form.description}
                onChange={handleInputChange}
                className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full text-base sm:text-sm border-gray-300 rounded-md"
                placeholder="e.g. Opening balances"
              />
            </div>

            {/* Date */}
            <div className="sm:col-span-1">
              <label
                htmlFor="date"
                className="block text-sm font-medium text-gray-700"
              >
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="date"
                name="date"
                required
                value={form.date}
                onChange={handleInputChange}
                className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full text-base sm:text-sm border-gray-300 rounded-md"
              />
            </div>

            {/* Reference */}
            <div className="sm:col-span-1">
              <label
                htmlFor="reference"
                className="block text-sm font-medium text-gray-700"
              >
                Reference
              </label>
              <input
                type="text"
                id="reference"
                name="reference"
                value={form.reference}
                onChange={handleInputChange}
                className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full text-base sm:text-sm border-gray-300 rounded-md"
                placeholder="e.g. OB-2025 (optional)"
              />
            </div>
          </dl>

          {/* Journal Lines */}
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              Journal Lines <span className="text-red-500">*</span>
            </h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Account
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Description
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                      Debit (£)
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                      Credit (£)
                    </th>
                    <th className="px-3 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {form.lines.map((line, index) => (
                    <tr key={line.id}>
                      <td className="px-3 py-2">
                        <select
                          className="block w-full text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          value={line.nominalCode}
                          onChange={(e) =>
                            handleLineChange(
                              index,
                              "nominalCode",
                              e.target.value,
                            )
                          }
                        >
                          <option value="">— select account —</option>
                          {allCategories
                            .filter((c) => !c.parentCode)
                            .map((parent) => (
                              <optgroup
                                key={parent.nominalCode}
                                label={`${parent.nominalCode} ${parent.name}`}
                              >
                                {allCategories
                                  .filter(
                                    (c) => c.parentCode === parent.nominalCode,
                                  )
                                  .map((child) => (
                                    <option
                                      key={child.nominalCode}
                                      value={child.nominalCode}
                                    >
                                      {child.nominalCode} – {child.name}
                                    </option>
                                  ))}
                              </optgroup>
                            ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          className="block w-full text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          value={line.description}
                          onChange={(e) =>
                            handleLineChange(
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                          placeholder="Line description"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="block w-full text-sm text-right border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          value={line.debit || ""}
                          onChange={(e) =>
                            handleLineChange(
                              index,
                              "debit",
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          placeholder="0.00"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="block w-full text-sm text-right border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          value={line.credit || ""}
                          onChange={(e) =>
                            handleLineChange(
                              index,
                              "credit",
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          placeholder="0.00"
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => removeLine(index)}
                          disabled={form.lines.length <= 2}
                          className="text-red-500 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                          title="Remove line"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr className="border-t-2 border-gray-300">
                    <td
                      colSpan={2}
                      className="px-3 py-2 text-sm font-medium text-gray-900"
                    >
                      <button
                        type="button"
                        onClick={addLine}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                      >
                        + Add Line
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">
                      {totalDebit.toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">
                      {totalCredit.toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                  <tr>
                    <td
                      colSpan={2}
                      className="px-3 py-2 text-sm font-medium text-gray-900"
                    >
                      Difference
                    </td>
                    <td
                      colSpan={2}
                      className={`px-3 py-2 text-right text-sm font-semibold ${
                        isBalanced ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {isBalanced
                        ? "✓ Balanced"
                        : `${(totalDebit - totalCredit).toFixed(2)} unbalanced`}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        <div className="px-4 py-4 sm:px-6 bg-gray-50 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => navigate("/dashboard/journal")}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 sm:py-2 border border-gray-300 rounded-md shadow-sm text-base sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none min-h-[44px] sm:min-h-0"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !isBalanced}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 sm:py-2 border border-transparent rounded-md shadow-sm text-base sm:text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none disabled:opacity-50 min-h-[44px] sm:min-h-0"
          >
            Save Entry
          </button>
        </div>
      </form>
    </div>
  );
};

export default JournalNewEntry;
